import { useEffect, useMemo, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, ArrowsClockwise, Clock, MapPin, Warning, ArrowSquareOut,
} from "@phosphor-icons/react";

// ObservationsV2Tab — admin moderation queue for the unified Observation
// Network. Approve / reject / archive submissions; every action is recorded
// in the observation's moderation_history.

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const KIND_STYLES = {
  nature: "bg-emerald-100 text-emerald-900 border-emerald-200",
  weather: "bg-sky-100 text-sky-900 border-sky-200",
  event: "bg-amber-100 text-amber-900 border-amber-200",
  road: "bg-orange-100 text-orange-900 border-orange-200",
  incident: "bg-rose-100 text-rose-900 border-rose-200",
};

function ModerationCard({ o, onDecide }) {
  const [busy, setBusy] = useState(false);
  const cover = o.media?.[0]?.url;
  const kind = o.category_kinds?.[0] || "nature";
  const decide = async (status) => {
    setBusy(true);
    try {
      await http.post(`/nature/observations/${o.id}/moderate`, { status });
      toast.success(`Marked ${status}`);
      onDecide?.(o.id, status);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-black/15 bg-black/5 p-4 flex gap-3" data-testid={`mod-obs-${o.id}`}>
      {cover && (
        <img src={abs(cover)} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />
      )}
      <div className="min-w-0 flex-1 text-[#151312]">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-flex rounded-full px-2 py-0.5 border font-mono-tech text-[9px] tracking-[0.22em] uppercase ${KIND_STYLES[kind] || "bg-stone-100 text-stone-900 border-stone-200"}`}>{kind}</span>
          <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">{(o.category_path || []).slice(-1)[0]?.label}</span>
          <span className="ml-auto font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{o.moderation_status}</span>
        </div>
        <div className="font-serif-display text-lg leading-tight">{o.title}</div>
        {o.description && <div className="mt-1 text-sm text-[#3A3830] line-clamp-2">{o.description}</div>}
        <div className="mt-1.5 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#3A3830]">
          <span className="flex items-center gap-1"><MapPin size={11} weight="fill" /> {o.location?.display_name || "—"}</span>
          <span>{o.date} {o.time}</span>
          <span>· {o.anonymous ? "Anonymous witness" : (o.reporter?.name || "Observer")}</span>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {["approved", "rejected", "archived", "pending"].map((s) => (
            <button
              key={s}
              disabled={busy || o.moderation_status === s}
              onClick={() => decide(s)}
              data-testid={`mod-decide-${o.id}-${s}`}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border disabled:opacity-40 ${
                s === "approved" ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" :
                s === "rejected" ? "border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100" :
                s === "archived" ? "border-stone-400 bg-stone-50 text-stone-800 hover:bg-stone-100" :
                "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
              }`}
            >
              {s === "approved" && <CheckCircle size={11} weight="fill" />}
              {s === "rejected" && <XCircle size={11} weight="fill" />}
              {s === "archived" && <ArrowsClockwise size={11} weight="bold" />}
              {s === "pending" && <Clock size={11} weight="fill" />}
              {s}
            </button>
          ))}
          <a
            href={`/observations/${o.id}`}
            target="_blank" rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 text-[#151312] font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
          >
            <ArrowSquareOut size={11} weight="bold" /> Open
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ObservationsV2Tab() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: tab, limit: "100" });
      const { data } = await http.get(`/nature/observations?${params}`);
      setItems(data?.observations || []);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const migrate = async () => {
    if (!window.confirm("Migrate legacy observations + civic_reports into v2? This is idempotent.")) return;
    setMigrating(true);
    try {
      const { data } = await http.post("/nature/migrate");
      toast.success(`Migrated ${data.migrated_observations} obs + ${data.migrated_civic_reports} reports · v2 total: ${data.total_v2}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Migration failed"); }
    finally { setMigrating(false); }
  };

  const onDecide = (id, status) => {
    // Optimistic — remove from current tab if it changed status
    if (tab !== "all" && status !== tab) setItems((it) => it.filter((x) => x.id !== id));
    else setItems((it) => it.map((x) => x.id === id ? { ...x, moderation_status: status } : x));
  };

  return (
    <div className="space-y-4" data-testid="obs-v2-tab">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            data-testid={`mod-tab-${s.key}`}
            className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
              tab === s.key ? "bg-[#E4A15C] text-[#151312] border-[#E4A15C]" : "border-black/15 text-[#3A3830] hover:bg-black/5"
            }`}
          >{s.label}</button>
        ))}
        <button
          onClick={migrate}
          disabled={migrating}
          data-testid="mod-migrate"
          className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 border border-black/15 text-[#151312] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-50"
        >
          <ArrowsClockwise size={11} weight="bold" /> {migrating ? "Migrating…" : "Migrate legacy"}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-[10px] tracking-widest text-[#3A3830]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-black/15 p-8 text-center text-[#3A3830]">
          Nothing in <b>{tab}</b> right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {items.map((o) => <ModerationCard key={o.id} o={o} onDecide={onDecide} />)}
        </div>
      )}
    </div>
  );
}
